import _ from 'lodash';

import * as actionTypes from '../actions/actionTypes';
import {updateObject, updateWithPrevData} from '../utility';
import {setQueryParam, setQueryParamArray} from '../../helpers/common';
import {getFilters} from '../../helpers/urlParsers/FiltersParser';
import encodeChars from '../../helpers/urlParsers/rules/encodeChars';
import LayersParser, {parseLayers} from '../../helpers/urlParsers/LayersParser';
import {addLayerToPath, mapLayersObject} from '../../utilities/utilitiesLayers';
import {getPath} from '../../utilities/utilitiesSearchGrid';

const getDefaultLayersState = () => ({
  layers: {},
  rawLayers: {},
  availableLandUses: {},
  checkedLayers: {},
  checkedJurisdictions: {},
  layersFetched: false,
  landUseLayersLoaded: false,
  showZoningLayers: false,
  showLayersLegend: true,
  updateLayers: false,
});
const getDefaultMapSearchResults = () => ({
  searchInited: false,
  filters: {},
  vehicleTypes: {},
  poiCategories: {},
  poiResultsCount: 0,
  transitResultsCount: 0,
  results: {},
  resultsLimited: false,
  clusters: {},
  shapes: {},
  bounds: null,
  clustersInited: false,
});

const getDefaultMapFormData = () => ({
  filters: {},
  formData: {},
  landUseOptions: {},
  listParams: {
    rows: 20,
    sort: 'asc',
    currentPage: 0,
    sortField: null,
  },
  enabledFields: {},
  limitState: false,
});

const initialState = {
  layers: getDefaultLayersState(),
  gridInitialized: false,
  placeBoundary: null,
  mapData: {},
  propertyPopupData: {},
  mapGeometry: {
    zoom: null,
    boundBox: null,
    prevZoomCenter: null,
    defaultBounds: null,
  },
  navigation: {
    state: '',
    county: '',
    place: null,
    property: '',
    stateObject: null,
    loadProperty: false,
    loadFeatures: false,
  },
  mode3d: false,
  userSavedLayerTemplates: {},
  currentLayerTemplate: '',
  userSavedBuildingsTemplates: {},
  placesSettings: {},
  currentBuildingsTemplate: '',
  userSavedFiltersTemplates: {},
  currentFiltersTemplate: '',
  mapSearchResults: getDefaultMapSearchResults(),
  mapSearchInProgress: false,
  mapFormData: getDefaultMapFormData(),
  propertyLists: [],
  openedPropertyState: 0,
  filtersLastValueUsed: {},
  recentProperties: {
    results: {},
    listParams: {
      rows: 20,
      sort: 'desc',
      currentPage: 0,
      sortField: 'timestamp',
    },
  },
  followedProperties: {
    results: {},
    listParams: {
      rows: 20,
      sort: 'asc',
      currentPage: 0,
      sortField: null,
    },
    lastReFetch: null,
    lastFetch: null,
  },
  propertiesNoteData: {},
  canUpdateParam: true,
  selectedPropertyRows: {},
  shortUrlMode: false,
};

const setLayersParam = (state, layers, updateParam) => {
  const layersParser = new LayersParser();
  // eslint-disable-next-line no-restricted-globals
  const [, parsedLayersParams] = layersParser.parseToUrl(layers, state.layers?.layers).getResult();
  Object.entries(parsedLayersParams).forEach(([key, value]) => {
    setQueryParam(key, value, updateParam);
  });
};

const addLayer = (state, action) => {
  const layers = {...state.layers.checkedLayers};
  const {group, subGroup, id} = action.layer;
  layers[group] = layers[group] || {};
  layers[group][subGroup] = layers[group][subGroup] || [];
  layers[group][subGroup].push(id);
  layers[group][subGroup] = _.uniq(layers[group][subGroup]);
  if (state.canUpdateParam) {
    setLayersParam(state, layers);
  }
  return updateObject(state, {layers: updateObject(state.layers, {checkedLayers: layers})});
};

const removeLayer = (state, action) => {
  const layers = {...state.layers.checkedLayers};
  const {group, subGroup, id} = action.layer;
  layers[group][subGroup] = _.without(layers[group][subGroup], id);
  if (!layers[group][subGroup].length) {
    delete layers[group][subGroup];
  }
  if (!_.size(layers[group])) {
    delete layers[group];
  }
  if (state.canUpdateParam) {
    setLayersParam(state, layers);
  }
  return updateObject(state, {layers: updateObject(state.layers, {checkedLayers: layers})});
};

const getZoningCheckedLayers = (state) => {
  const zoningCheckedLayers = {};
  Object.entries(state.layers.layers?.groups || {}).forEach(([, groupValue]) => {
    if (!groupValue.subgroups) return;
    Object.entries(groupValue.subgroups).forEach(([, subgroupValue]) => {
      if (subgroupValue?.land_use_types?.length > 0 && subgroupValue.land_use_types.includes('municipal-zoning-code')) {
        Object.entries(subgroupValue.layers).forEach(([, layerValue]) => {
          const id = layerValue.layer_type === 'tile' ? layerValue.tile_id : layerValue.layer_id;
          if (!zoningCheckedLayers[groupValue.group]) {
            zoningCheckedLayers[groupValue.group] = {};
          }
          if (!zoningCheckedLayers[groupValue.group][subgroupValue.subgroup]) {
            zoningCheckedLayers[groupValue.group][subgroupValue.subgroup] = [];
          }
          zoningCheckedLayers[groupValue.group][subgroupValue.subgroup].push(id);
        });
      }
    });
  });

  return zoningCheckedLayers;
};
const getStableCheckedLayers = (state) => {
  const stableCheckedLayers = {};
  Object.entries(state.layers.layers?.groups || {}).forEach(([, groupValue]) => {
    if (!groupValue.subgroups) return;
    Object.entries(groupValue.subgroups).forEach(([, subgroupValue]) => {
      Object.entries(subgroupValue.layers).forEach(([, layerValue]) => {
        if (layerValue.layer_enabled) {
          const id = layerValue.layer_type === 'tile' ? layerValue.tile_id : layerValue.layer_id;

          if (!stableCheckedLayers[groupValue.group]) {
            stableCheckedLayers[groupValue.group] = {};
          }

          if (!stableCheckedLayers[groupValue.group][subgroupValue.subgroup]) {
            stableCheckedLayers[groupValue.group][subgroupValue.subgroup] = [];
          }

          stableCheckedLayers[groupValue.group][subgroupValue.subgroup].push(id);
        }
      });
    });
  });

  return stableCheckedLayers;
};

const switchLayers = (state, action) => {
  const {layers} = action;
  const stableCheckedLayers = getStableCheckedLayers(state);
  const zoningCheckedLayers = getZoningCheckedLayers(state);
  const result = {
    checkedLayers: updateObject(
      {
        ...stableCheckedLayers,
        ...zoningCheckedLayers,
      },
      layers,
    ),
    updateLayers: true,
  };
  if (action.shouldBeParsed) {
    const [newLayers, landUseLayers] = parseLayers(state.layers?.layers, layers);
    result.checkedLayers = newLayers;
    if (Object.values(landUseLayers).length > 0) {
      result.checkedJurisdictions = landUseLayers;
    }
  }
  if (action.updateParam) {
    setLayersParam(state, result.checkedLayers, action.updateParam);
  }

  return updateObject(state, {
    layers: updateObject(state.layers, result),
    canUpdateParam: !!action.updateParam,
  });
};

const resetLayers = (state, action) => {
  if (action.updateParam) {
    setLayersParam(state, {}, action.updateParam);
  }
  const stableCheckedLayers = getStableCheckedLayers(state);

  return updateObject(state, {
    layers: updateObject(state.layers, {
      checkedLayers: stableCheckedLayers,
      checkedJurisdictions: {},
      updateLayers: true,
    }),
    canUpdateParam: !!action.updateParam,
  });
};

const controlLayersSet = (state, action) => {
  const {checkedLayers} = state.layers;
  const {enable, disable} = action;
  const newCheckedLayers = {};

  mapLayersObject(checkedLayers, (name, subname, layer) => {
    if (!disable?.[name]?.[subname]?.includes(layer)) {
      addLayerToPath(newCheckedLayers, name, subname, layer);
    }
  });

  mapLayersObject(enable, (name, subname, layer) => {
    addLayerToPath(newCheckedLayers, name, subname, layer);
  });

  return updateObject(state, {
    layers: updateObject(state.layers, {
      checkedLayers: newCheckedLayers,
      updateLayers: true,
    }),
  });
};

const setLayersJurisdictions = (state, action) => {
  let newData = action.data;
  if (typeof newData === 'function') {
    newData = newData(state.layers);
  }

  return updateObject(state, {
    layers: updateObject(state.layers, {checkedJurisdictions: newData}),
  });
};

const setMap3dMode = (state, action) => {
  if (action.updateParam) {
    // Remove from url as streets is used by default.
    setQueryParam('mode3d', action.mode3d, action.updateParam);
  }
  return updateObject(state, {mode3d: action.mode3d});
};

const setRecentProperties = (state, action) => {
  let newData = action.data;
  if (typeof newData === 'function') {
    newData = newData(state.recentProperties);
  }
  if (!action.initial && newData.results) {
    localStorage.setItem('recentProperties', JSON.stringify(newData.results));
  }
  return updateObject(state, {recentProperties: updateObject(state.recentProperties, newData)});
};

const updateMapSearchResults = (state, action) => {
  let newState = action.mapSearchResults;
  if (typeof newState === 'function') {
    newState = newState(state.mapSearchResults);
  }
  return updateObject(state, {
    mapSearchResults: updateObject(state.mapSearchResults, newState),
    mapSearchInProgress: false,
  });
};

const resetMapSearchResults = (state, action) => {
  setQueryParam('formData', {}, 2);
  const paramArr = [];
  // eslint-disable-next-line no-unused-expressions
  getFilters(state.mapFormData.filters)?.forEach(({name, settings}) => {
    if (settings.type === 'range_filter' || settings.type === 'date_filter') {
      paramArr.push({name: `${encodeChars.colon(name)}-min`, value: ''});
      paramArr.push({name: `${encodeChars.colon(name)}-max`, value: ''});
    }
    paramArr.push({name: encodeChars.colon(name), value: ''});
  });
  setQueryParamArray(paramArr, 2);
  const defaultMapFormData = getDefaultMapFormData();
  const currentFormData = getDefaultMapFormData();

  return updateObject(state, {
    mapSearchResults: getDefaultMapSearchResults(),
    mapFormData: {
      ...defaultMapFormData,
      formData:
        action.saveProperty && currentFormData.id_by_address
          ? {...defaultMapFormData.formData, id_by_address: currentFormData.id_by_address}
          : defaultMapFormData.formData,
      filters: state.mapFormData.filters,
    },
  });
};

const setPropertyLists = (state, action) => {
  const sorted = _.sortBy(action.data, item => (item.title === 'Default' ? -1 : 0));
  return updateObject(state, {propertyLists: sorted});
};

const setSelectedPropertyRows = (state, action) => {
  let newState = action.data;
  if (typeof newState === 'function') {
    newState = newState(state.selectedPropertyRows);
  }
  return updateObject(state, {selectedPropertyRows: newState});
};
const getState = item =>
  item
    .toString()
    .split('-')?.[0]
    ?.toUpperCase();
// Reducer function.
const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_LAYER:
      return addLayer(state, action);
    case actionTypes.REMOVE_LAYER:
      return removeLayer(state, action);
    case actionTypes.RESET_LAYERS:
      return resetLayers(state, action);
    case actionTypes.STORE_ALL_LAYERS:
      return updateWithPrevData(state, action, 'layers', 'data', true);
    case actionTypes.SWITCH_LAYERS:
      return switchLayers(state, action);
    case actionTypes.SET_MODE_3D:
      return setMap3dMode(state, action);
    case actionTypes.LAYERS_UPDATE_START:
      return updateObject(state, {layers: updateObject(state.layers, {updateLayers: true})});
    case actionTypes.LAYERS_UPDATE_DONE:
      return updateObject(state, {layers: updateObject(state.layers, {updateLayers: false})});
    case actionTypes.SWITCH_LAYERS_LEGEND:
      return updateObject(state, {
        layers: updateObject(state.layers, {showLayersLegend: action.state}),
      });
    case actionTypes.SET_LAYERS_JURISDICTIONS:
      return setLayersJurisdictions(state, action);
    case actionTypes.CONTROL_CHECKED_LAYERS:
      return controlLayersSet(state, action);
    case actionTypes.FETCH_MAP_DATA:
      return updateObject(state, {mapData: action.data});
    case actionTypes.SET_PLACE_BOUNDARY:
      return updateObject(state, {placeBoundary: action.data});
    case actionTypes.SET_GRID_INITIALIZED:
      return updateObject(state, {gridInitialized: action.data});
    case actionTypes.SET_PROPERTY_POPUP_DATA:
      return updateWithPrevData(state, action, 'propertyPopupData', 'propertyPopupData');
    case actionTypes.SET_MAP_GEOMETRY:
      return updateObject(state, {
        mapGeometry: updateObject(state.mapGeometry, action.mapGeometry),
      });
    case actionTypes.FETCH_USER_SAVED_LAYER_TEMPLATES:
      return updateObject(state, {userSavedLayerTemplates: action.data});
    case actionTypes.CURRENT_LAYER_TEMPLATE:
      return updateObject(state, {currentLayerTemplate: action.name});
    case actionTypes.FETCH_USER_SAVED_BUILDINGS_TEMPLATES:
      return updateObject(state, {userSavedBuildingsTemplates: action.data});
    case actionTypes.CURRENT_BUILDINGS_TEMPLATE:
      return updateObject(state, {currentBuildingsTemplate: action.name});
    case actionTypes.FETCH_USER_SAVED_FILTERS_TEMPLATES:
      return updateObject(state, {userSavedFiltersTemplates: action.data});
    case actionTypes.FETCH_PLACES:
      return updateObject(state, {placesSettings: action.data});
    case actionTypes.CURRENT_FILTERS_TEMPLATE:
      return updateObject(state, {currentFiltersTemplate: action.name});
    case actionTypes.SUBMIT_MAP_SEARCH:
      return updateObject(state, {mapSearchInProgress: true});
    case actionTypes.UPDATE_MAP_SEARCH_RESULTS:
      return updateMapSearchResults(state, action);
    case actionTypes.RESET_MAP_SEARCH_RESULTS:
      return resetMapSearchResults(state, action);
    case actionTypes.SET_MAP_FORM_DATA:
      return updateWithPrevData(state, action, 'mapFormData', 'mapFormData', true);
    case actionTypes.SET_PROPERTY_LISTS:
      return setPropertyLists(state, action);
    case actionTypes.SET_SELECTED_PROPERTY_ROWS:
      return setSelectedPropertyRows(state, action);
    case actionTypes.OPENED_PROPERTY_STATE:
      return updateObject(state, {openedPropertyState: action.state});
    case actionTypes.SET_RECENT_PROPERTIES:
      return setRecentProperties(state, action);
    case actionTypes.SET_FOLLOWED_PROPERTIES:
      return updateWithPrevData(state, action, 'followedProperties', 'data', true);
    case actionTypes.SET_PROPERTIES_NOTE_DATA: {
      return updateWithPrevData(state, action, 'propertiesNoteData');
    }
    case actionTypes.SET_NAVIGATION_STATE: {
      return updateObject(state, {
        navigation: {
          ...state.navigation,
          state: action.state,
          stateObject: action.stateObject !== '' ? action.stateObject : state.navigation.stateObject,
        },
      });
    }
    case actionTypes.SET_NAVIGATION_COUNTY: {
      const updatedObject = {
        county: action.county,
      };
      if (action.county) {
        updatedObject.state = getState(action.county.placeId);
      }
      return updateObject(state, {
        navigation: {
          ...state.navigation,
          ...updatedObject,
        },
      });
    }
    case actionTypes.SET_NAVIGATION_PLACE: {
      const updatedObject = {
        place: action.place
          ? {
            ...action.place,
            path: getPath(action.place.path || action.place.url || ''),
          }
          : null,
      };
      if (action.place) {
        updatedObject.state = getState(action.place.placeId);
        if (action.county) {
          updatedObject.county = action.county;
        }
      }
      return updateObject(state, {
        navigation: {
          ...state.navigation,
          ...updatedObject,
          loadFeatures: action.load,
        },
      });
    }
    case actionTypes.SET_NAVIGATION_PROPERTY: {
      const updatedObject = {
        property: action.property,
      };
      if (action.property) {
        updatedObject.state = getState(action.property.id);
        if (action.county) {
          updatedObject.county = action.county;
        }
      }
      return updateObject(state, {
        navigation: {
          ...state.navigation,
          ...updatedObject,
          loadProperty: action.load,
        },
      });
    }
    case actionTypes.SET_UPDATE_PARAM_MAP:
      return updateObject(state, {canUpdateParam: action.state});
    case actionTypes.INIT_CLUSTERS:
      return updateObject(state, {
        mapSearchResults: updateObject(state.mapSearchResults, {clustersInited: true}),
        mapSearchInProgress: false,
      });
    case actionTypes.TOGGLE_ZONING_LAYERS:
      return updateObject(state, {
        layers: {
          ...state.layers,
          showZoningLayers: action.show,
        },
      });
    case actionTypes.SET_FILTERS_LAST_VALUE_USE:
      return updateObject(state, {
        filtersLastValueUsed: !action.name
          ? action.value
          : updateObject(state.filtersLastValueUsed, {
            [action.name]: action.value,
          }),
      });

    case actionTypes.SET_SHORT_URL_MODE:
      return updateObject(state, {shortUrlMode: !!action.value});

    default:
      return state;
  }
};

export default reducer;
